package com.snakesworld.game.ui

import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.snakesworld.game.ui.theme.LocalGameCanvasColors
import com.snakesworld.game.ui.theme.appFontFamily
import com.snakesworld.game.viewmodel.ConnectingStatus
import com.snakesworld.game.viewmodel.ConnectingViewModel
import org.jetbrains.compose.resources.stringResource
import snakesworld.shared.generated.resources.Res
import snakesworld.shared.generated.resources.connecting_error
import snakesworld.shared.generated.resources.multiplayer_requires_network
import snakesworld.shared.generated.resources.connecting_handshake
import snakesworld.shared.generated.resources.connecting_title
import snakesworld.shared.generated.resources.retry_connection
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

@Composable
fun ConnectingScreen(
    viewModel: ConnectingViewModel,
    userId: String = "",
    callsign: String,
    skinId: String,
    onConnected: (assignedId: String) -> Unit,
    onBackToMenu: () -> Unit,
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val canvasColors = LocalGameCanvasColors.current

    LaunchedEffect(Unit) {
        viewModel.connect(callsign = callsign, skinId = skinId, userId = userId)
    }

    LaunchedEffect(uiState.status) {
        val status = uiState.status
        if (status is ConnectingStatus.Connected) {
            onConnected(status.assignedUserId)
            viewModel.reset()
        }
    }

    val infiniteTransition = rememberInfiniteTransition(label = "radar")
    val sweepAngle by infiniteTransition.animateFloat(
        initialValue = 0f,
        targetValue = 360f,
        animationSpec = infiniteRepeatable(
            animation = tween(2000, easing = LinearEasing),
            repeatMode = RepeatMode.Restart
        ),
        label = "sweep"
    )

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(canvasColors.backgroundColor)
            .windowInsetsPadding(WindowInsets.safeDrawing),
        contentAlignment = Alignment.Center
    ) {
        ScreenBackButton(
            onBack = {
                viewModel.reset()
                onBackToMenu()
            },
            modifier = Modifier
                .align(Alignment.TopStart)
                .padding(16.dp),
            canvasColors = canvasColors,
        )
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
            modifier = Modifier.padding(24.dp)
        ) {
            // Retro Radar Scanner
            Box(
                modifier = Modifier.size(160.dp),
                contentAlignment = Alignment.Center
            ) {
                Canvas(modifier = Modifier.fillMaxSize()) {
                    val center = Offset(size.width / 2f, size.height / 2f)
                    val r = size.width / 2f - 10f

                    drawCircle(
                        color = canvasColors.gridColor,
                        radius = r,
                        style = androidx.compose.ui.graphics.drawscope.Stroke(2f)
                    )
                    drawCircle(
                        color = canvasColors.gridColor,
                        radius = r * 0.66f,
                        style = androidx.compose.ui.graphics.drawscope.Stroke(1f)
                    )
                    drawCircle(
                        color = canvasColors.gridColor,
                        radius = r * 0.33f,
                        style = androidx.compose.ui.graphics.drawscope.Stroke(1f)
                    )

                    // Radar sweep line
                    val rad = sweepAngle * (PI.toFloat() / 180f)
                    val target = Offset(center.x + r * cos(rad), center.y + r * sin(rad))
                    drawLine(
                        color = canvasColors.hudTextColor,
                        start = center,
                        end = target,
                        strokeWidth = 3f
                    )
                }
            }

            Spacer(modifier = Modifier.height(28.dp))

            when (val status = uiState.status) {
                is ConnectingStatus.Connected,
                is ConnectingStatus.Connecting -> {
                    Text(
                        text = stringResource(Res.string.connecting_title),
                        color = canvasColors.hudTextColor,
                        fontSize = 18.sp,
                        fontWeight = FontWeight.Bold,
                        fontFamily = appFontFamily(),
                        letterSpacing = 1.sp
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        text = stringResource(Res.string.connecting_handshake),
                        color = canvasColors.accentColor,
                        fontSize = 12.sp,
                        fontFamily = appFontFamily()
                    )
                }

                is ConnectingStatus.Error -> {
                    Text(
                        text = stringResource(Res.string.connecting_error),
                        color = canvasColors.dangerColor,
                        fontSize = 18.sp,
                        fontWeight = FontWeight.Black,
                        fontFamily = appFontFamily()
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        text = stringResource(Res.string.multiplayer_requires_network),
                        color = canvasColors.hudTextColor.copy(alpha = 0.85f),
                        fontSize = 12.sp,
                        fontFamily = appFontFamily(),
                        textAlign = androidx.compose.ui.text.style.TextAlign.Center,
                        modifier = Modifier.padding(horizontal = 24.dp),
                    )
                    Spacer(modifier = Modifier.height(24.dp))

                    Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        Button(
                            onClick = {
                                viewModel.connect(
                                    callsign = callsign,
                                    skinId = skinId,
                                    userId = userId
                                )
                            },
                            colors = ButtonDefaults.buttonColors(
                                containerColor = canvasColors.hudBgColor,
                                contentColor = canvasColors.hudTextColor,
                            ),
                            shape = RoundedCornerShape(6.dp),
                            modifier = Modifier.border(
                                1.dp,
                                canvasColors.hudTextColor,
                                RoundedCornerShape(6.dp)
                            )
                        ) {
                            Text(
                                stringResource(Res.string.retry_connection),
                                color = canvasColors.hudTextColor,
                                fontFamily = appFontFamily()
                            )
                        }

                    }
                }
            }
        }
    }
}
